import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi, apiError, inr, API, LOGO_URL } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RazorpayButton } from "@/components/RazorpayButton";
import { LogOut, Upload, Download, Plus, Trash2, Users, FileCheck, Package, Wallet, Images, Play, Route } from "lucide-react";
import { GuideBookButton } from "@/components/GuideBookButton";
import { PaymentStages } from "@/components/PaymentStages";
import { WalletCard } from "@/components/WalletCard";
import { AddressCard } from "@/components/AddressCard";
import { PriceTag, offerText } from "@/components/PriceTag";

const DOC_TYPES = ["Passport Copy", "Photo", "Payment Slip / Bank Transfer", "Other"];
const DOWNLOADABLE = ["Umrah E-Visa (KSA)", "Flight PNR & Ticket", "NOC / Ministry Clearance", "Payment Slip / Invoice"];

export default function PortalHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [packages, setPackages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [payment, setPayment] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [media, setMedia] = useState([]);
  const [groups, setGroups] = useState([]);
  const [journey, setJourney] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [paySubs, setPaySubs] = useState([]);
  const [payForm, setPayForm] = useState({ amount: "", note: "" });
  const payFileRef = React.useRef(null);
  const [uploadType, setUploadType] = useState(DOC_TYPES[0]);
  const [pax, setPax] = useState({ first_name: "", last_name: "", passport_no: "", passport_expiry: "", dob: "", gender: "Male" });

  const loadAll = async () => {
    try {
      const [m, pk, d, p, pax, md, gr, jr, it, sub] = await Promise.all([
        portalApi.get("/portal/me"), portalApi.get("/portal/packages"), portalApi.get("/portal/documents"),
        portalApi.get("/portal/payment"), portalApi.get("/portal/passengers"), portalApi.get("/portal/media"), portalApi.get("/portal/groups"),
        portalApi.get("/portal/journey"), portalApi.get("/portal/itinerary"), portalApi.get("/portal/payment/submissions"),
      ]);
      setMe(m.data); setPackages(pk.data.packages || []); setDocs(d.data); setPayment(p.data); setPassengers(pax.data); setMedia(md.data);
      setGroups(gr.data); setJourney(jr.data); setItinerary(it.data); setPaySubs(sub.data);
    } catch {
      navigate("/portal");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("portal_token")) { navigate("/portal"); return; }
    loadAll();
    // eslint-disable-next-line
  }, []);

  const logout = () => { localStorage.removeItem("portal_token"); navigate("/portal"); };

  const submitPayment = async () => {
    const file = payFileRef.current?.files?.[0];
    if (!payForm.amount || !file) { toast.error("Enter amount and attach the receipt"); return; }
    const fd = new FormData();
    fd.append("amount", payForm.amount);
    fd.append("note", payForm.note);
    fd.append("receipt", file);
    try {
      await portalApi.post("/portal/payment/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt submitted — awaiting confirmation");
      setPayForm({ amount: "", note: "" });
      if (payFileRef.current) payFileRef.current.value = "";
      setPaySubs((await portalApi.get("/portal/payment/submissions")).data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("doc_type", uploadType); fd.append("file", file);
    try {
      await portalApi.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded successfully");
      setDocs((await portalApi.get("/portal/documents")).data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const download = (doc) => window.open(`${API}/portal/documents/${doc.id}/download?auth=${localStorage.getItem("portal_token")}`, "_blank");

  const downloadBill = async () => {
    try {
      const { data } = await portalApi.post("/portal/invoice");
      toast.success(`Bill slip ready (${data.invoice_no})`);
      setDocs((await portalApi.get("/portal/documents")).data);
      window.open(`${API}/portal/documents/${data.id}/download?auth=${localStorage.getItem("portal_token")}`, "_blank");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addPax = async () => {
    if (!pax.first_name || !pax.last_name) { toast.error("Name & surname required"); return; }
    try {
      await portalApi.post("/portal/passengers", pax);
      toast.success("Passenger added");
      setPax({ first_name: "", last_name: "", passport_no: "", passport_expiry: "", dob: "", gender: "Male" });
      setPassengers((await portalApi.get("/portal/passengers")).data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const delPax = async (id) => {
    await portalApi.delete(`/portal/passengers/${id}`);
    setPassengers(passengers.filter((p) => p.id !== id));
  };

  const joinGroup = async (gid) => {
    try {
      await portalApi.post("/portal/join-group", { group_id: gid });
      toast.success("Travel date confirmed! You are added to the group.");
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const requestSeat = async (gid) => {
    try {
      const { data } = await portalApi.post("/portal/waitlist", { group_id: gid });
      toast.success(data.message || "Added to waitlist");
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Loading…</div>;

  const paid = (payment?.ledger || []).reduce((s, l) => s + l.amount, 0);
  const balance = (payment?.total || 0) - paid;
  const myDownloads = docs.filter((d) => DOWNLOADABLE.includes(d.type));

  return (
    <div className="relative min-h-screen">
      <PageBackground />
      <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border/60 glass sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="logo" className="w-10 h-10 rounded-full ring-1 ring-gold/50" />
          <div><p className="font-serif text-sm font-semibold">{me.name}</p><p className="eyebrow text-gold/80">Pilgrim Portal{me.customer_no ? ` · Customer #${me.customer_no}` : ""}</p></div>
        </div>
        <GuideBookButton testid="portal-guide-btn" small href={`${API}/prospectus/guide.pdf?origin=${encodeURIComponent(window.location.origin)}`} />
        <Button onClick={logout} variant="ghost" size="sm" data-testid="portal-logout" className="text-destructive gap-1.5"><LogOut className="w-4 h-4" /> Exit</Button>
      </header>

      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
        {payment && (
          <div className="grid grid-cols-4 gap-3">
            <Card className="p-3 bg-card/40 border-border/60"><p className="eyebrow text-muted-foreground">Package</p><p className="text-sm font-semibold mt-1 truncate">{payment.package}</p></Card>
            <Card className="p-3 bg-card/40 border-border/60"><p className="eyebrow text-muted-foreground">Total</p><p className="text-sm font-serif text-white mt-1">{inr(payment.total)}</p></Card>
            <Card className="p-3 bg-card/40 border-emerald-500/30"><p className="eyebrow text-muted-foreground">Paid</p><p className="text-sm font-serif text-emerald-300 mt-1">{inr(paid)}</p></Card>
            <Card className="p-3 bg-card/40 border-gold/30"><p className="eyebrow text-muted-foreground">Balance</p><p className="text-sm font-serif text-gold mt-1">{inr(balance)}</p></Card>
          </div>
        )}

        {journey && (
          <Card className="p-4 bg-card/40 border-gold/30" data-testid="journey-tracker">
            <p className="eyebrow text-gold mb-3">Your Journey Status</p>
            <div className="flex flex-wrap gap-y-3">
              {journey.stages.map((s, i) => {
                const curIdx = journey.stages.indexOf(journey.stage);
                const done = i <= curIdx;
                const isCur = i === curIdx;
                return (
                  <div key={s} data-testid={`journey-step-${i}`} className="flex items-center">
                    <div className="flex flex-col items-center" style={{ width: 92 }}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${done ? "bg-gold text-[#0a2e22]" : "bg-primary/40 text-muted-foreground border border-border/60"} ${isCur ? "ring-2 ring-gold/50" : ""}`}>
                        {done ? "✓" : i + 1}
                      </div>
                      <p className={`text-[9px] text-center mt-1.5 leading-tight ${done ? "text-white" : "text-muted-foreground"}`}>{s}</p>
                    </div>
                    {i < journey.stages.length - 1 && <div className={`h-0.5 w-4 ${i < curIdx ? "bg-gold" : "bg-border/60"}`} />}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {payment?.plan && <PaymentStages plan={payment.plan} />}

        <WalletCard client={portalApi} token={localStorage.getItem("portal_token")} />
        <AddressCard client={portalApi} />

        <Card className="p-4 bg-card/40 border-gold/30" data-testid="portal-pay-card">
          <p className="eyebrow text-gold mb-2">Make a Payment</p>
          <PaymentInfoCard />
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            <Input data-testid="pay-amount-input" type="number" placeholder="Amount paid (₹)" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="bg-card/60" />
            <Input data-testid="pay-note-input" placeholder="Reference / note (optional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="bg-card/60" />
          </div>
          <input ref={payFileRef} type="file" accept="image/*,application/pdf" data-testid="pay-receipt-input" className="mt-2 text-xs text-muted-foreground" />
          <Button data-testid="pay-submit-btn" onClick={submitPayment} className="w-full mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5"><Upload className="w-4 h-4" /> Submit Payment Receipt</Button>
          {balance > 0 && <RazorpayButton token={localStorage.getItem("portal_token")} body={{ kind: "pilgrim", amount: Math.min(Number(payForm.amount) || payment?.plan?.current?.remaining || balance, balance) }}
            label={`Pay ${inr(Math.min(Number(payForm.amount) || payment?.plan?.current?.remaining || balance, balance))}${!payForm.amount && payment?.plan?.current ? ` (${payment.plan.current.label})` : ""} online (UPI / card / netbanking)`} testid="portal-razorpay-btn" className="w-full mt-2" onPaid={() => window.location.reload()} />}
          {paySubs.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {paySubs.map((s) => (
                <div key={s.id} data-testid={`pay-sub-${s.id}`} className="flex items-center justify-between text-xs border-t border-border/40 pt-1.5">
                  <span className="text-white">{inr(s.amount)}{s.note ? ` · ${s.note}` : ""}</span>
                  <Badge variant="outline" className={s.status === "accepted" ? "border-emerald-500/40 text-emerald-300" : s.status === "rejected" ? "border-destructive/40 text-destructive" : "border-amber-500/40 text-amber-300"}>{s.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 bg-card/40 border-gold/30" data-testid="umrah-date-card">
          <p className="eyebrow text-gold">Your Umrah Date</p>
          {me.group ? (
            <p className="text-sm mt-1.5">
              <span className="text-white font-medium">{me.group.name}</span>
              {me.group.package && <span className="text-gold"> · {me.group.package}</span>}
              {me.group.date && <span className="text-muted-foreground"> · {new Date(me.group.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1.5">Not set yet — open the Packages tab, choose a package and pick an available date.</p>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">Dates & seats are managed by Bengal Umrah House. To change, contact us on WhatsApp.</p>
        </Card>

        <Tabs defaultValue="passengers">
          <TabsList className="bg-card/50 border border-gold/25 w-full grid grid-cols-6">
            <TabsTrigger value="passengers" data-testid="portal-tab-passengers" className="text-xs gap-1"><Users className="w-3.5 h-3.5" /> Group</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="portal-tab-downloads" className="text-xs gap-1"><Download className="w-3.5 h-3.5" /> Downloads</TabsTrigger>
            <TabsTrigger value="upload" data-testid="portal-tab-upload" className="text-xs gap-1"><Upload className="w-3.5 h-3.5" /> Upload</TabsTrigger>
            <TabsTrigger value="packages" data-testid="portal-tab-packages" className="text-xs gap-1"><Package className="w-3.5 h-3.5" /> Packages</TabsTrigger>
            <TabsTrigger value="itinerary" data-testid="portal-tab-itinerary" className="text-xs gap-1"><Route className="w-3.5 h-3.5" /> Itinerary</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="portal-tab-gallery" className="text-xs gap-1"><Images className="w-3.5 h-3.5" /> Gallery</TabsTrigger>
          </TabsList>

          <TabsContent value="itinerary" className="mt-4 space-y-2" data-testid="portal-itinerary">
            <p className="eyebrow text-gold">Day-by-Day Itinerary</p>
            {itinerary.length === 0 && <p className="text-sm text-muted-foreground">Your itinerary will appear here once published by Bengal Umrah House.</p>}
            {itinerary.map((it) => (
              <Card key={it.id} className="p-3 bg-card/40 border-border/60 flex gap-3">
                <div className="w-14 flex-shrink-0 text-center"><p className="eyebrow text-gold">{it.day}</p></div>
                <div className="min-w-0"><p className="text-sm text-white font-medium">{it.title}</p>{it.detail && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.detail}</p>}</div>
              </Card>
            ))}
          </TabsContent>

          {/* Passengers */}
          <TabsContent value="passengers" className="mt-4 space-y-3">
            <Card className="p-4 bg-card/40 border-border/60">
              <p className="eyebrow text-gold mb-3">Add Pilgrim / Family Member</p>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="pax-first" placeholder="First name" value={pax.first_name} onChange={(e) => setPax({ ...pax, first_name: e.target.value })} className="bg-card/60" />
                <Input data-testid="pax-last" placeholder="Surname" value={pax.last_name} onChange={(e) => setPax({ ...pax, last_name: e.target.value })} className="bg-card/60" />
                <Input data-testid="pax-passport" placeholder="Passport no" value={pax.passport_no} onChange={(e) => setPax({ ...pax, passport_no: e.target.value })} className="bg-card/60" />
                <div><Label className="text-[10px] text-muted-foreground">Passport expiry</Label><Input type="date" value={pax.passport_expiry} onChange={(e) => setPax({ ...pax, passport_expiry: e.target.value })} className="bg-card/60 h-9" /></div>
                <div><Label className="text-[10px] text-muted-foreground">Date of birth</Label><Input type="date" value={pax.dob} onChange={(e) => setPax({ ...pax, dob: e.target.value })} className="bg-card/60 h-9" /></div>
                <div><Label className="text-[10px] text-muted-foreground">Gender</Label>
                  <Select value={pax.gender} onValueChange={(v) => setPax({ ...pax, gender: v })}>
                    <SelectTrigger className="bg-card/60 h-9" data-testid="pax-gender"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <Button data-testid="pax-add-btn" onClick={addPax} disabled={passengers.length >= 5} className="w-full mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5 disabled:opacity-50"><Plus className="w-4 h-4" /> Add Passenger</Button>
              <p className="text-[10px] text-muted-foreground mt-2 text-center" data-testid="pax-limit-note">
                {passengers.length >= 5 ? "Limit reached — maximum 5 family members per customer ID." : `You can add up to 5 family members (${passengers.length}/5 added).`}
              </p>
            </Card>
            <div className="space-y-2">
              <p className="eyebrow text-gold">Your Group ({passengers.length} of 5)</p>
              {passengers.map((p) => (
                <Card key={p.id} data-testid={`portal-pax-${p.id}`} className="p-3 bg-card/40 border-border/60 flex items-center gap-3">
                  <div className="flex-1"><p className="text-sm font-medium">{p.first_name} {p.last_name}</p><p className="text-[11px] text-muted-foreground font-mono">{p.passport_no || "no passport"} · {p.gender} · DOB {p.dob || "—"} · Exp {p.passport_expiry || "—"}</p></div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delPax(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </Card>
              ))}
              {passengers.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No passengers added yet.</p>}
            </div>
          </TabsContent>

          {/* Downloads */}
          <TabsContent value="downloads" className="mt-4 space-y-2">
            {payment && (
              <Card className="p-3 bg-gradient-to-r from-primary/50 to-card border border-gold/30 flex items-center justify-between">
                <div><p className="text-sm font-medium text-white">Your Payment Bill / Slip</p><p className="text-[11px] text-emerald-100/70">Auto-generated with your package & balance</p></div>
                <Button size="sm" data-testid="portal-bill-btn" onClick={downloadBill} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5 text-xs"><Wallet className="w-3.5 h-3.5" /> Download Bill</Button>
              </Card>
            )}
            <p className="eyebrow text-gold">Your Documents (Visa, Ticket, NOC, Slip)</p>
            {myDownloads.map((d) => (
              <Card key={d.id} data-testid={`portal-doc-${d.id}`} className="p-3 bg-card/40 border-border/60 flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-gold" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{d.type}</p><p className="text-[11px] text-muted-foreground truncate">{d.original_filename}</p></div>
                <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[10px]">{d.status}</Badge>
                <Button size="sm" onClick={() => download(d)} className="bg-gold text-[#0a2e22] hover:bg-gold/90 gap-1.5 text-xs"><Download className="w-3.5 h-3.5" /> Get</Button>
              </Card>
            ))}
            {myDownloads.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Your visa, ticket & NOC will appear here once issued by Bengal Umrah House.</p>}
          </TabsContent>

          {/* Upload */}
          <TabsContent value="upload" className="mt-4 space-y-3">
            <Card className="p-4 bg-card/40 border-border/60">
              <Label className="text-xs text-muted-foreground">Document type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger className="mt-1 bg-card/60" data-testid="portal-upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <label className="block mt-3">
                <input type="file" className="hidden" onChange={upload} data-testid="portal-upload-input" accept=".pdf,.png,.jpg,.jpeg,.webp" />
                <span className="inline-flex w-full justify-center items-center gap-2 px-4 py-2.5 rounded-md bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-sm cursor-pointer"><Upload className="w-4 h-4" /> Upload Passport / Payment Slip</span>
              </label>
            </Card>
            <div className="space-y-2">
              {docs.filter((d) => !DOWNLOADABLE.includes(d.type)).map((d) => (
                <Card key={d.id} className="p-3 bg-card/40 border-border/60 flex items-center gap-3">
                  <Upload className="w-4 h-4 text-gold" />
                  <div className="flex-1 min-w-0"><p className="text-sm truncate">{d.type}</p><p className="text-[11px] text-muted-foreground truncate">{d.original_filename}</p></div>
                  <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[10px]">{d.status}</Badge>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Packages */}
          <TabsContent value="packages" className="mt-4 space-y-2">
            <p className="eyebrow text-gold">Our Umrah Packages</p>
            <PaymentInfoCard />
            {packages.map((p) => {
              const pkgGroups = groups.filter((g) => g.package === p.name);
              return (
                <Card key={p.id} data-testid={`portal-package-${p.id}`} className="p-4 bg-card/40 border-border/60">
                  <div className="flex items-center justify-between flex-wrap gap-1"><p className="font-semibold">{p.name}</p><PriceTag price={p.price} mrp={p.mrp} until={p.offer_until} testid={`portal-price-${p.id}`} /></div>
                  <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
                  {pkgGroups.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="eyebrow text-muted-foreground">Available dates & seats</p>
                      {pkgGroups.map((g) => (
                        <div key={g.id} data-testid={`portal-group-${g.id}`} className="flex items-center justify-between gap-2 bg-card/60 rounded-md px-2.5 py-2">
                          <div className="min-w-0">
                            <p className="text-xs text-white">{new Date(g.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{g.price > 0 && <span data-testid={`portal-group-price-${g.id}`} className="text-gold font-semibold"> · {inr(g.price)}{offerText(g.price, g.mrp, g.offer_until) && <><span className="ml-1 line-through text-white/50 font-normal">{inr(g.mrp)}</span><span className="ml-1 text-[10px] text-red-300">{offerText(g.price, g.mrp, g.offer_until)}</span></>}</span>}</p>
                            <p className="text-[10px] text-muted-foreground">{g.seats_left === null ? "Seats open" : g.seats_left > 0 ? `${g.seats_left} of ${g.seats_total} seats left` : "Full"}</p>
                            {g.muallem_name && (
                              <p data-testid={`portal-group-muallem-${g.id}`} className="text-[10px] text-emerald-300 mt-0.5 flex items-center gap-1">
                                {g.muallem_has_photo ? (
                                  <img src={`${API}/muallems/${g.muallem_id}/photo?auth=${localStorage.getItem("portal_token")}`} alt={g.muallem_name} className="w-4 h-4 rounded-full object-cover ring-1 ring-emerald-400/40" />
                                ) : (
                                  <span className="w-4 h-4 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-[8px] font-serif text-emerald-300">{g.muallem_name[0]}</span>
                                )}
                                Muallem: {g.muallem_name}
                              </p>
                            )}
                          </div>
                          {g.joined ? (
                            <span data-testid={`portal-joined-${g.id}`} className="text-[10px] text-gold font-semibold px-2">Your date ✓</span>
                          ) : g.seats_left === 0 ? (
                            g.waitlisted ? (
                              <span data-testid={`portal-waitlisted-${g.id}`} className="text-[10px] text-amber-300 font-semibold px-2">Waitlisted ✓</span>
                            ) : (
                              <Button size="sm" data-testid={`portal-waitlist-${g.id}`} onClick={() => requestSeat(g.id)} className="bg-amber-500/20 border border-amber-400/40 text-amber-300 hover:bg-amber-500/30 font-semibold text-[11px] h-7 px-3">Request Seat</Button>
                            )
                          ) : (
                            <Button size="sm" data-testid={`portal-join-${g.id}`} onClick={() => joinGroup(g.id)} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-[11px] h-7 px-3">Choose</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </TabsContent>

          {/* Gallery */}
          <TabsContent value="gallery" className="mt-4 space-y-3" data-testid="portal-gallery-tab-content">
            <p className="eyebrow text-gold">Our Umrah Tours — Photos & Videos</p>
            {media.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Photos & videos of our Umrah tours will appear here soon.</p>}
            <div className="grid grid-cols-2 gap-3">
              {media.map((i) => (
                <Card key={i.id} data-testid={`portal-media-${i.id}`} className="overflow-hidden bg-card/40 border-border/60">
                  <div className="aspect-video bg-black/40 flex items-center justify-center overflow-hidden">
                    {i.kind === "video" ? (
                      <video src={`${API}/portal/media/${i.id}/file?auth=${localStorage.getItem("portal_token")}`} controls className="w-full h-full object-cover" />
                    ) : (
                      <img src={`${API}/portal/media/${i.id}/file?auth=${localStorage.getItem("portal_token")}`} alt={i.caption || i.original_filename} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <p className="p-2 text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                    {i.kind === "video" ? <Play className="w-3 h-3 text-gold flex-shrink-0" /> : null}
                    {i.caption || i.original_filename}
                  </p>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PaymentInfoCard() {
  const [info, setInfo] = useState(null);
  useEffect(() => { fetch(`${API}/public/payment-info`).then((r) => r.json()).then(setInfo).catch(() => {}); }, []);
  if (!info || (!info.account_number && !info.has_qr && !info.upi_id)) return null;
  return (
    <Card data-testid="portal-payment-info" className="p-4 bg-gradient-to-r from-primary/50 to-card border border-gold/30">
      <p className="eyebrow text-gold mb-2">How to Pay</p>
      <div className="flex items-center gap-4">
        {info.has_qr && <img src={`${API}/public/payment-info/qr`} alt="Payment QR" className="w-24 h-24 rounded-lg border border-gold/40 object-cover" />}
        <div className="text-xs text-emerald-100/80 space-y-0.5">
          {info.account_name && <p className="text-white font-medium">{info.account_name}</p>}
          {info.bank_name && <p>{info.bank_name}</p>}
          {info.account_number && <p className="font-mono">A/C: {info.account_number}</p>}
          {info.ifsc && <p className="font-mono">IFSC: {info.ifsc}</p>}
          {info.upi_id && <p className="font-mono">UPI: {info.upi_id}</p>}
        </div>
      </div>
    </Card>
  );
}
